import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Network, ArrowLeft, Calculator, Lightbulb, 
  GitGraph, FileText, Settings, Rocket, Target, Zap, Layers, Puzzle, BrainCircuit,
  CalendarCheck, Check, MessageSquareMore, ShieldCheck
} from 'lucide-react';

// --- IMÁGENES DEL CARRUSEL ---
const EO_CAROUSEL_IMAGES = [
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80',
  'https://zaphy.com.co/wp-content/uploads/2026/01/estructura-organizacional-2.jpg'
];

const EstructuraOrganizacional = ({ language }) => { 
  
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % EO_CAROUSEL_IMAGES.length);
    }, 4000); 
    return () => clearInterval(interval);
  }, []);

  // --- DICCIONARIO DE TRADUCCIONES CON ESTRATEGIA DE UPSELL DIGITAL Y DENSIDAD SEO ---
  const t = {
    es: {
      back: 'Volver al Inicio',
      heroTag: 'Consultoría de Procesos Organizacionales y Arquitectura Empresarial',
      heroTitle: 'El Orden que Necesitas,',
      heroSubtitle: 'para Escalar tu Negocio',
      heroDesc: 'Pasa del caos de "todos hacen todo" a una estructura eficiente y rentable. Definimos roles, estandarizamos tus procesos e inyectamos transformación digital para que tu PyME funcione con precisión milimétrica.',
      heroBtnQuote: 'Cotizar Estructuración',
      heroBtnWhatsApp: 'Escríbenos por WhatsApp',
      imgBadgeTitle: 'Procesos',
      imgBadgeDesc: 'Optimizados',
      painTag: 'Caos Interno',
      painTitle: '¿Sientes que tu empresa está estancada y depende 100% de ti?',
      painDesc: 'El crecimiento desordenado genera síntomas críticos: duplicidad de funciones, cuellos de botella operativos, colaboradores desalineados y procesos que solo viven en la cabeza del dueño. Sin orden, la operación colapsa y el negocio frena su facturación.',
      painHighlight: 'Sin cimientos sólidos no hay crecimiento sostenible. Organizamos la arquitectura interna de tu empresa para que puedas escalar al siguiente nivel.',
      painResultTitle: 'Resultado',
      painResultDesc: 'Claridad Total',
      
      // Banner de Co-Responsabilidad Operativa Zaphy
      bannerTitle: 'Ingeniería Corporativa y Reestructuración de Procesos',
      bannerDesc: 'En Zaphy no entregamos manuales teóricos para archivar. Intervenimos tu operación de forma real: diseñamos organigramas estratégicos, alineamos las cargas laborales y establecemos tableros de control (KPIs) para que puedas medir la eficiencia de cada área en tiempo real.',
      
      gridTitle: 'Pilares del Rediseño Organizacional',
      gridSubtitle: 'Construimos las columnas administrativas y los flujos operativos que garantizan la rentabilidad.',
      cards: [
        { title: 'Manuales de Funciones & Roles', desc: 'Documentamos perfiles de empleo, responsabilidades y alcances por cargo para eliminar zonas grises, duplicidad de tareas y excusas operativas.' },
        { title: 'Organigramas Estratégicos', desc: 'Diseñamos líneas de mando y estructuras jerárquicas o matriciales claras que faciliten la comunicación asertiva y la toma de decisiones.' },
        { title: 'Mapeo & Estandarización', desc: 'Estructuramos el "cómo se hace". Creamos diagramas de flujo y manuales de procedimiento que optimizan tiempos y facilitan el onboarding.' },
        { title: 'Reglamento Interno de Trabajo', desc: 'Blindaje jurídico laboral (RIT). Establecemos deberes, derechos y escalas sancionatorias claras para una convivencia legal y sana.' },
        { title: 'Tableros de Control & KPIs', desc: 'Lo que no se mide no se puede mejorar. Diseñamos indicadores de gestión clave para auditar la productividad real de cada área.' },
        { title: 'Alineación de Cultura Corporal', desc: 'Estructuramos Misión, Visión y Valores corporativos. Conectamos el propósito del negocio con el ADN y metas de tus colaboradores.' }
      ],
      
      // Sección de Innovación (Puente para Páginas Web, SEO y Marketing)
      // --- SECCIÓN 4 EN ESPAÑOL: ENFOQUE INNOVACIÓN Y AUTOMATIZACIÓN DE PROCESOS ---
      innovTag: 'Transformación y Eficiencia',
      innovTitle: 'Innovación Empresarial & Digitalización de Procesos',
      innovDesc: 'Un organigrama moderno necesita herramientas modernas. No solo ordenamos la estructura de tu empresa en el papel, sino que diseñamos los flujos de trabajo del futuro. Conectamos tus manuales de funciones y procesos operativos con la tecnología, implementando herramientas digitales, automatizaciones de tareas repetitivas y sistemas integrados que eliminan el trabajo manual, optimizan el tiempo de tu equipo y permiten que tu negocio opere con total autonomía.',
      changeMgmtTitle: 'Gestión del Cambio Cultural',
      changeMgmtDesc: 'Preparamos y entrenamos a tu equipo de trabajo para adoptar nuevas tecnologías y procesos sin resistencia operativa.',
      agileTitle: 'Metodologías Ágiles (Scrum/Kanban)',
      agileDesc: 'Implementamos marcos de trabajo modernos para que tus departamentos dejen de operar aislados y se conviertan en células autónomas y veloces.',
      
      seoTitle: 'Preguntas Frecuentes sobre Estructura Organizacional para PyMEs',
      seoSubtitle: 'Resolvemos tus dudas sobre ingeniería de procesos, cultura corporativa y escalabilidad digital.',
      faqs: [
        { q: '¿Cuándo sabe un empresario que es momento de contratar una consultoría de estructura organizacional?', a: 'El momento crítico es cuando el dueño del negocio se convierte en el cuello de botella de la operación; es decir, si la empresa no vende o no opera si el gerente no está presente. También cuando hay síntomas de fatiga interna, tareas duplicadas, procesos lentos o cuando el negocio ha alcanzado un tope de facturación y no logra escalar.' },
        { q: '¿Qué es el mapeo de procesos y cómo beneficia a la rentabilidad de mi PyME?', a: 'Consiste en diagramar de forma visual y técnica el paso a paso de cada operación (ventas, compras, servicio al cliente). Esto permite identificar desperdicios de tiempo, eliminar pasos innecesarios y automatizar tareas repetitivas, lo que se traduce de forma directa en una reducción de costos de operación y mayor margen de ganancia.' },
        { q: '¿Por qué la estructura interna se relaciona con el marketing digital y las páginas web?', a: 'Porque de nada sirve inyectar leads a través de campañas de marketing digital o tener una página web si tu estructura interna no está lista para atender esa demanda en orden. Al organizar tus procesos, definimos los canales digitales correctos para que los prospectos fluyan automáticamente desde la web hacia tu equipo de ventas sin perder datos en el camino.' },
        { q: '¿Cuánto tiempo toma ver resultados en una reestructuración organizacional?', a: 'Los resultados visuales en claridad y definición de roles son inmediatos desde la entrega de los manuales de funciones y organigramas. La estabilización total del sistema y la adopción de metodologías ágiles o herramientas digitales suele consolidarse en un periodo de 30 a 60 días de acompañamiento.' },
        { q: '¿Zaphy nos ayuda a implementar las herramientas tecnológicas que se recomienden?', a: '¡Totalmente! En Zaphy unimos la ingeniería de procesos con la transformación digital. No solo te decimos qué debes mejorar, sino que diseñamos y ejecutamos las soluciones de automatización, optimización SEO y ecosistemas web necesarios para que tu nueva estructura corporativa ruede sola.' }
      ],
      ctaTitle: '¿Listo para organizar tu estructura y automatizar tu éxito?',
      ctaDesc: 'Una empresa organizada es una empresa escalable y altamente rentable. Elige el camino para iniciar tu transformación hoy mismo.',
      ctaBtnQuote: 'Cotizar Servicio',
      ctaBtnSchedule: 'Agendar Asesoría',
      ctaBtnWhatsApp: 'Hablemos por WhatsApp'
    },
    en: {
      back: 'Back to Home',
      heroTag: 'Enterprise Architecture Consulting & Process Engineering',
      heroTitle: 'The Order You Need,',
      heroSubtitle: 'to Scale Your Business',
      heroDesc: 'Move from the chaos of "everyone does everything" to an efficient and profitable structure. We define roles, standardize your processes, and inject digital transformation so your PyME runs with military precision.',
      heroBtnQuote: 'Quote Structuring',
      heroBtnWhatsApp: 'Chat on WhatsApp',
      imgBadgeTitle: 'Processes',
      imgBadgeDesc: 'Optimized',
      painTag: 'Internal Chaos',
      painTitle: 'Do you feel your company is stagnant and depends 105% on you?',
      painDesc: 'Disordered growth generates critical symptoms: duplicated duties, operational bottlenecks, misaligned collaborators, and processes that only live in the owners head. Without order, operation collapses and business halts its invoicing.',
      painHighlight: 'Without solid foundations, there is no sustainable growth. We organize your enterprise internal architecture so you can scale to the next level.',
      painResultTitle: 'Result',
      painResultDesc: 'Total Clarity',
      
      bannerTitle: 'Corporate Engineering & Process Restructuring',
      bannerDesc: 'At Zaphy, we do not deliver theoretical manuals to be filed away. We intervene in your operation in a real way: we design strategic organizational charts, align labor workloads, and establish control dashboards (KPIs) so you can measure the efficiency of each area in real time.',
      
      gridTitle: 'Pillars of Organizational Redesign',
      gridSubtitle: 'We build the administrative columns and operational flows that guarantee profitability.',
      cards: [
        { title: 'Job Manuals & Roles', desc: 'We document job profiles, responsibilities, and scopes per position to eliminate gray areas, duplicated tasks, and operational excuses.' },
        { title: 'Strategic Org Charts', desc: 'We design clear lines of command and hierarchical or matrix structures that facilitate communication and decision-making.' },
        { title: 'Process Mapping & Standarization', desc: 'We structure the "how-to". We create flowcharts and procedural manuals that optimize times and facilitate onboarding.' },
        { title: 'Internal Labor Regulations', desc: 'Labor legal shielding (RIT). We establish clear duties, rights, and sanction scales for a healthy and legal coexistence.' },
        { title: 'Control Dashboards & KPIs', desc: 'What is not measured cannot be improved. We design key performance indicators to audit the real productivity of each area.' },
        { title: 'Corporate Culture Alignment', desc: 'We structure mission, vision, and corporate values. We connect the business purpose with your collaborators DNA and goals.' }
      ],
      
      // --- SECCIÓN 4 EN INGLÉS: ENFOQUE INNOVACIÓN Y AUTOMATIZACIÓN DE PROCESOS ---
      innovTag: 'Transformation & Efficiency',
      innovTitle: 'Business Innovation & Process Digitalization',
      innovDesc: 'A modern organizational chart needs modern tools. We do not just arrange your company structure on paper; we design the workflows of the future. We connect your job manuals and operational processes with technology, implementing digital tools, repetitive task automation, and integrated systems that eliminate manual labor, optimize your team time, and allow your business to operate with full autonomy.',
      changeMgmtDesc: 'We prepare and train your work team to adopt new technologies and processes without operational resistance.',
      agileTitle: 'Agile Methodologies (Scrum/Kanban)',
      agileDesc: 'Implementation of agile frameworks (Scrum/Kanban) for faster and autonomous teams.',
      
      seoTitle: 'Frequently Asked Questions about Org Structure for PyMEs',
      seoSubtitle: 'We solve your doubts about process engineering, corporate culture, and digital scalability.',
      faqs: [
        { q: 'When does a business owner know it is time to hire an organizational structure consulting?', a: 'The critical moment is when the business owner becomes the bottleneck of the operation; that is, if the company does not sell or operate if the manager is not present. Also when there are symptoms of internal fatigue, duplicated tasks, slow processes, or when the business has reached a revenue cap.' },
        { q: 'What is process mapping and how does it directly benefit my PyMEs profitability?', a: 'It consists of visually mapping out step-by-step each operation (sales, purchases, customer service). This allows the identification of time wastes, elimination of unnecessary steps, and automation of repetitive tasks, translating into cost reduction.' },
        { q: 'Why is internal structure related to digital marketing and web pages?', a: 'Because it is useless to inject leads through digital marketing campaigns or have a web page if your internal structure is not ready to meet that demand in order. By organizing your processes, we define the right digital channels so that prospects flow automatically.' },
        { q: 'How long does it take to see results in an organizational restructuring?', a: 'Visual results in clarity and definition of roles are immediate from the delivery of job manuals and org charts. Total system stabilization and the adoption of agile methodologies or digital tools usually consolidate in a 30 to 60-day period.' },
        { q: 'Does Zaphy help us implement the technological tools that are recommended?', a: 'Totally! At Zaphy, we bridge process engineering with digital transformation. We do not just tell you what to improve, we design and execute the automation solutions, SEO optimization, and web ecosystems required.' }
      ],
      ctaTitle: 'Ready to organize your structure and automate your success?',
      ctaDesc: 'An organized company is a scalable and highly profitable company. Choose the path to start your transformation today.',
      ctaBtnQuote: 'Quote Service',
      ctaBtnSchedule: 'Schedule Advisory',
      ctaBtnWhatsApp: 'Chat via WhatsApp'
    }
  };

  const text = t[language] || t.es;

  const handleWhatsAppRedirect = () => {
    const targetUrl = `https://api.whatsapp.com/send?phone=573114044203&text=${encodeURIComponent("¡Hola, Zaphy! 🚀 Estuve revisando el servicio de Estructura Organizacional en su web. Me interesa organizar mis procesos internos y conocer cómo articularlo con automatizaciones y marketing digital.")}`;
    window.open(targetUrl, '_blank');
  };

  return (
    <div className="animate-[fadeIn_0.5s_ease-out] pt-32 pb-20 min-h-screen bg-gray-50 font-[Poppins]">
      
      {/* --- 1. HERO SECTION --- */}
      <div className="relative bg-[#0F172A] text-white py-24 overflow-hidden mb-16">
         <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')"}}></div>
         <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#8B0C3C]/60"></div>
         
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors font-medium">
               <ArrowLeft size={20} /> {text.back}
            </button>
            
            <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-[#8B0C3C]/30 text-[#ff4d94] border border-[#8B0C3C]/50">
                            <Network size={40} />
                        </div>
                        <span className="text-[#ff80b3] font-bold tracking-widest uppercase text-xs md:text-sm">{text.heroTag}</span>
                    </div>
                    
                    <h1 className="text-3xl sm:text-5xl font-extrabold mb-6 leading-tight">
                    {text.heroTitle} <br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e01464] to-[#ff4d94]">{text.heroSubtitle}</span>
                    </h1>
                    
                    <p className="text-xl text-gray-300 max-w-2xl leading-relaxed mb-8">
                    {text.heroDesc}
                    </p>

                    <div className="flex flex-wrap gap-4">
                        <button 
                          onClick={() => navigate('/cotizador')} 
                          className="bg-[#8B0C3C] text-white font-bold py-4 px-8 rounded-2xl shadow-lg shadow-[#8B0C3C]/40 transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-[#6d092f] text-sm uppercase tracking-wide"
                        >
                            <Calculator size={20}/> {text.heroBtnQuote}
                        </button>

                        <button 
                          onClick={handleWhatsAppRedirect} 
                          className="bg-transparent border-2 border-[#8B0C3C] text-[#ff4d94] font-bold py-4 px-8 rounded-2xl transition-all flex items-center gap-2 transform hover:-translate-y-1 hover:bg-[#8B0C3C] hover:text-white shadow-lg shadow-[#8B0C3C]/5 text-sm uppercase tracking-wide"
                        >
                            <MessageSquareMore size={20}/> {text.heroBtnWhatsApp}
                        </button>
                    </div>
                </div>

                <div className="w-full max-w-md mt-10 md:mt-0">
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-[#8B0C3C] to-[#e01464] rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-[#0F172A]">
                             <img 
                                src="https://zaphy.com.co/wp-content/uploads/2026/01/estructuraOrganizacional.png" 
                                alt="Estructura Organizacional Zaphy" 
                                className="w-full h-auto object-cover transform transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent opacity-60"></div>
                            <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 p-3 rounded-xl flex items-center gap-3">
                                <div className="bg-[#8B0C3C] p-1.5 rounded-full text-white"><GitGraph size={16} /></div>
                                <div>
                                    <p className="text-xs text-pink-200 uppercase font-bold">{text.imgBadgeTitle}</p>
                                    <p className="text-sm font-bold text-white">{text.imgBadgeDesc}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
         </div>
      </div>

      {/* --- 2. EL PROBLEMA --- */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-xl border border-gray-100 flex flex-col lg:flex-row items-center gap-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#8B0C3C]/10 rounded-bl-[100px] z-0"></div>

              <div className="lg:w-1/2 relative z-10">
                  <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-full font-bold text-sm mb-4 border border-red-100">
                      <Puzzle size={16} /> {text.painTag}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{text.painTitle}</h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">{text.painDesc}</p>
                  <p className="font-bold text-gray-800 border-l-4 border-[#8B0C3C] pl-4">{text.painHighlight}</p>
              </div>

              <div className="w-full lg:w-1/2 relative z-10 mt-12 lg:mt-0">
                  <div className="absolute -inset-4 bg-[#8B0C3C]/20 rounded-[40px] transform -rotate-2 z-0"></div>
                  
                  <div className="relative z-10 rounded-[30px] shadow-lg w-full h-64 sm:h-80 overflow-hidden bg-gray-200">
                      {EO_CAROUSEL_IMAGES.map((img, idx) => (
                          <img 
                            key={idx}
                            src={img} 
                            alt={`Organización Empresarial ${idx + 1}`} 
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${idx === currentImage ? 'opacity-100' : 'opacity-0'}`}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600/8B0C3C/FFFFFF?text=Estructura'; }}
                          />
                      ))}

                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                          {EO_CAROUSEL_IMAGES.map((_, idx) => (
                              <div 
                                key={idx} 
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentImage ? 'bg-[#8B0C3C] w-4' : 'bg-white/50'}`}
                              />
                          ))}
                      </div>
                  </div>

                  <div className="absolute -bottom-6 -left-6 z-20 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-full text-green-600"><Target size={24}/></div>
                      <div><p className="text-xs text-gray-500 font-bold uppercase">{text.painResultTitle}</p><p className="text-sm font-bold text-gray-900">{text.painResultDesc}</p></div>
                  </div>
              </div>
          </div>
      </div>

      {/* --- BANNER ESTRATÉGICO --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-[30px] p-8 md:p-10 border border-slate-700/50 shadow-xl flex flex-col md:flex-row items-center gap-8">
            <div className="bg-[#8B0C3C]/20 p-4 rounded-2xl text-[#ff4d94] shrink-0 border border-[#8B0C3C]/30">
               <ShieldCheck size={40} />
            </div>
            <div>
               <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{text.bannerTitle}</h3>
               <p className="text-slate-300 text-sm md:text-base leading-relaxed">{text.bannerDesc}</p>
            </div>
         </div>
      </div>

      {/* --- 3. GRID DE SERVICIOS --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{text.gridTitle}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{text.gridSubtitle}</p>
         </div>
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {text.cards.map((card, idx) => {
               const configs = [
                 { icon: FileText, border: 'hover:border-blue-500', iconBg: 'bg-blue-50', text: 'text-blue-600' },
                 { icon: GitGraph, border: 'hover:border-purple-500', iconBg: 'bg-purple-50', text: 'text-purple-600' },
                 { icon: Settings, border: 'hover:border-orange-500', iconBg: 'bg-orange-50', text: 'text-orange-600' },
                 { icon: Layers, border: 'hover:border-red-500', iconBg: 'bg-red-50', text: 'text-red-600' },
                 { icon: Target, border: 'hover:border-emerald-500', iconBg: 'bg-emerald-50', text: 'text-emerald-600' },
                 { icon: Zap, border: 'hover:border-pink-500', iconBg: 'bg-pink-50', text: 'text-pink-600' }
               ];
               const config = configs[idx] || configs[0];
               const Icon = config.icon;
               return (
                <div key={idx} className={`bg-white p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all group ${config.border}`}>
                    <div className={`w-14 h-14 ${config.iconBg} ${config.text} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                        <Icon size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{card.desc}</p>
                </div>
               );
            })}
         </div>
      </div>

      {/* --- 4. INNOVACIÓN & TRANSFORMACIÓN DIGITAL (¡EFECTO PANTALLA COMPLETA TOTAL DE EXTREMO A EXTREMO!) --- */}
      <div className="w-screen left-1/2 -translate-x-1/2 relative bg-[#8B0C3C] text-white py-24 overflow-hidden mb-24">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/circuit.png')]"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-16">
                  <div className="lg:w-1/2">
                      <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full font-bold text-sm mb-6 text-pink-200">
                          <Rocket size={16} /> {text.innovTag}
                      </div>
                      <h2 className="text-4xl sm:text-5xl font-extrabold mb-6">{text.innovTitle}</h2>
                      <p className="text-lg text-pink-100 mb-8 leading-relaxed">{text.innovDesc}</p>
                      <div className="space-y-6">
                          <div className="flex items-start gap-4">
                              <div className="bg-white/10 p-3 rounded-xl"><BrainCircuit size={24} className="text-pink-300"/></div>
                              <div><h4 className="font-bold text-xl">{text.changeMgmtTitle}</h4><p className="text-sm text-pink-100/80">{text.changeMgmtDesc}</p></div>
                          </div>
                          <div className="flex items-start gap-4">
                              <div className="bg-white/10 p-3 rounded-xl"><Lightbulb size={24} className="text-pink-300"/></div>
                              <div><h4 className="font-bold text-xl">{text.agileTitle}</h4><p className="text-sm text-pink-100/80">{text.agileDesc}</p></div>
                          </div>
                      </div>
                  </div>
                  <div className="lg:w-1/2 relative">
                      <div className="absolute -inset-4 bg-gradient-to-tr from-pink-500 to-purple-600 rounded-full blur-3xl opacity-40 animate-pulse"></div>
                      <div className="relative bg-white/5 backdrop-blur-md border border-white/10 p-2 rounded-3xl shadow-2xl">
                          <img src="https://zaphy.com.co/wp-content/uploads/2026/01/innovacion10.jpeg" alt="Ecosistemas Digitales y Desarrollo de Páginas Web | Zaphy" className="rounded-2xl w-full"/>
                      </div>
                  </div>
              </div>
          </div>
      </div>

      {/* --- 4.5: BLOQUE DE PREGUNTAS FRECUENTES (SEO BOOST SEMÁNTICO) --- */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
         <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{text.seoTitle}</h2>
            <p className="text-gray-500 text-xs md:text-sm">{text.seoSubtitle}</p>
         </div>
         <div className="space-y-4">
            {text.faqs.map((faq, idx) => (
               <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-slate-200 transition-all">
                  <h3 className="text-sm md:text-base font-bold text-gray-950 mb-2 flex items-start gap-2">
                     <span className="text-pink-600 shrink-0">❓</span> {faq.q}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm leading-relaxed pl-6">{faq.a}</p>
               </div>
            ))}
         </div>
      </div>

      {/* --- 5. CTA FINAL (TRIPLE ACCIÓN CON ACCESO DIRECTO A WHATSAPP COMPLETO) --- */}
      <div className="max-w-4xl mx-auto px-4 text-center">
         <div className="bg-gradient-to-br from-[#0F172A] to-[#8B0C3C] rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#8B0C3C]/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
            
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-6 relative z-10">{text.ctaTitle}</h2>
            <p className="text-pink-100 text-lg mb-8 max-w-2xl mx-auto relative z-10">{text.ctaDesc}</p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
                <button 
                  onClick={() => navigate('/cotizador')} 
                  className="bg-white text-[#8B0C3C] font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-pink-50 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <Calculator size={20}/>
                    {text.ctaBtnQuote}
                </button>

                <button 
                    onClick={handleWhatsAppRedirect}
                    className="bg-emerald-600 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:bg-emerald-700 transition-all transform hover:-translate-y-1 relative z-10 flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <MessageSquareMore size={20}/>
                    {text.ctaBtnWhatsApp}
                </button>
                
                <a 
                  href="https://calendar.app.google/UYZ2ERubo552yRg19" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-transparent border-2 border-white text-white font-bold py-4 px-8 rounded-xl hover:bg-white/10 transition-all flex items-center justify-center gap-2 text-xs md:text-sm uppercase tracking-wide"
                >
                    <CalendarCheck size={20}/>
                    {text.ctaBtnSchedule}
                </a>
            </div>
         </div>
      </div>

    </div>
  );
};

export default EstructuraOrganizacional;